import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { optimizedImage } from "@/lib/image";
import {
  FADE_IN_UP_HIDDEN,
  FADE_IN_UP_HIDDEN_SHORT,
  FADE_IN_VISIBLE,
  OPACITY_HIDDEN,
  OPACITY_VISIBLE,
  TRANSITION_SLOW,
} from "@/lib/animations";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/85q1nnlb_logo%20final%20final%20au%20bon%20debarras%20.png";
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/wr4iy8ju_image%20carton%20.jpg";

// Static animation constants — extracted to avoid creating new objects on every render
const LOGO_HIDDEN = { opacity: 0, y: -30 };
const T_DELAY_2 = { duration: 0.8, delay: 0.2 };
const T_DELAY_3 = { duration: 0.8, delay: 0.3 };
const T_DELAY_5 = { duration: 0.8, delay: 0.5 };
const T_DELAY_8 = { duration: 0.8, delay: 0.8 };

const tiles = [
  { id: "vide-maison", label: "Vide Maison", path: "/vide-maison" },
  { id: "boutique", label: "Boutique", path: "/boutique" },
  { id: "rachat-cash", label: "Rachat Cash", path: "/rachat-cash" },
  { id: "histoire-partenaires", label: "Histoire & Partenaire", path: "/histoire-partenaires" },
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={optimizedImage(BG_IMAGE, { w: 1920, q: 75 })}
          alt="Au Bon Débarras - Boutique"
          className="w-full h-full object-cover"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A3728]/65 via-[#4A3728]/55 to-[#4A3728]/75" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12">
        {/* Logo */}
        <motion.div
          initial={LOGO_HIDDEN}
          animate={FADE_IN_VISIBLE}
          transition={TRANSITION_SLOW}
          className="mb-8"
        >
          <img
            src={optimizedImage(LOGO_URL, { w: 640, q: 85, output: "png" })}
            alt="Au Bon Débarras"
            className="h-56 md:h-72 lg:h-80 w-auto"
            loading="eager"
            decoding="async"
          />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={FADE_IN_UP_HIDDEN_SHORT}
          animate={FADE_IN_VISIBLE}
          transition={T_DELAY_2}
          className="font-serif text-4xl md:text-6xl text-white text-center mb-4"
        >
          Au Bon Débarras
        </motion.h1>

        <motion.p
          initial={FADE_IN_UP_HIDDEN_SHORT}
          animate={FADE_IN_VISIBLE}
          transition={T_DELAY_3}
          className="text-xl md:text-2xl text-[#D4A373] font-serif text-center mb-12"
        >
          On est là pour vous&nbsp;!
        </motion.p>

        {/* Four Tiles */}
        <motion.div
          initial={FADE_IN_UP_HIDDEN}
          animate={FADE_IN_VISIBLE}
          transition={T_DELAY_5}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 w-full max-w-6xl"
        >
          {tiles.map((tile) => (
            <button
              key={tile.id}
              onClick={() => navigate(tile.path)}
              className="group relative bg-white/10 backdrop-blur-md border-2 border-white/30 hover:border-[#D4A373] hover:bg-white/20 rounded-2xl px-6 py-10 md:px-4 md:py-12 transition-all duration-500"
              data-testid={`btn-${tile.id}`}
            >
              <div className="flex flex-col items-center justify-center min-h-[80px]">
                <h2 className="font-serif text-xl md:text-2xl text-white text-center leading-snug">
                  {tile.label}
                </h2>
              </div>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#D4A373]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={OPACITY_HIDDEN}
          animate={OPACITY_VISIBLE}
          transition={T_DELAY_8}
          className="mt-16 text-center"
        >
          <a
            href="tel:0608691129"
            className="block text-white/90 hover:text-[#D4A373] transition-colors text-lg"
          >
            06 08 69 11 29
          </a>
          <p className="block text-white/80 text-sm mt-2 select-text">
            aubondebarras1@gmail.com
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default LandingPage;
